/**
 * Device / preference capability detection for the 3D system.
 *
 * Implements the four-tier degradation ladder from the performance contract:
 *   1. prefers-reduced-motion: reduce      -> poster only, no canvas, ever
 *   2. no WebGL2 support                   -> poster only
 *   3. hardwareConcurrency<=4 or deviceMemory<=4 -> reduced counts + frameloop="demand"
 *   4. Save-Data                           -> poster only
 */
import { useEffect, useState } from 'react';

const DEFAULT = {
  ready: false,
  posterOnly: true,
  reducedMotion: false,
  webgl2: false,
  lowPower: false,
  saveData: false,
  isMobile: false,
  finePointer: false,
  particleBudget: 2000,
  forceDemand: false,
};

let cached = null;

function detectWebGL2() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2'));
  } catch (err) {
    return false;
  }
}

export function detectCapability() {
  if (cached) return cached;
  if (typeof window === 'undefined') return DEFAULT;

  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  const isMobile = window.innerWidth <= 640;
  const connection = navigator.connection || navigator.webkitConnection || {};
  const saveData = Boolean(connection.saveData);
  const cores = navigator.hardwareConcurrency || 8;
  const memory = navigator.deviceMemory || 8;
  const lowPower = cores <= 4 || memory <= 4;
  const webgl2 = detectWebGL2();

  const posterOnly = reducedMotion || saveData || !webgl2;
  const particleBudget = isMobile || lowPower ? 2000 : 6000;

  cached = {
    ready: true,
    posterOnly,
    reducedMotion,
    webgl2,
    lowPower,
    saveData,
    isMobile,
    finePointer,
    particleBudget,
    forceDemand: lowPower,
  };
  return cached;
}

export function useCapability() {
  const [capability, setCapability] = useState(() =>
    typeof window === 'undefined' ? DEFAULT : detectCapability()
  );
  useEffect(() => {
    setCapability(detectCapability());
  }, []);
  return capability;
}

/** Reduced motion, as a standalone hook for non-3D reveals and counters. */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (event) => setReduced(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}
